import React, { useState } from 'react';
import {
    Box,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormHelperText,
    Chip,
    Typography,
    Paper,
    Button,
    IconButton,
} from '@mui/material';
import { StyledDropzone } from './Dropzone';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import { apiClient } from '../../helper/api';
import useAuth from '../../context/AuthProvider';
import Swal from 'sweetalert2';
import { GreenBtnColor, PrimaryBlueColor } from '../../helper/theme';

const sectionTitleSx = {
    color: 'text.secondary',
    fontWeight: 600,
    letterSpacing: '0.08em',
    display: 'block',
    mb: 1,
};
const paperSectionSx = {
    p: 2,
    borderRadius: 2,
    borderColor: 'divider',
    backgroundColor: 'grey.50',
};

export interface UploadSignatureRequestDocumentsProps {
    open: boolean;
    onClose: () => void;
    signatureRequestId: number;
    onSuccess: () => void;
}

export const UploadSignatureRequestDocuments: React.FC<UploadSignatureRequestDocumentsProps> = ({
    open,
    onClose,
    signatureRequestId,
    onSuccess,
}) => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);

    const handleClose = () => {
        setUploadedDocuments([]);
        onClose();
    };

    const handleSendToMisc = () => {
        if (uploadedDocuments.length === 0) {
            Swal.fire({
                title: 'Required',
                text: 'Please upload at least one document before sending to MISC.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        Swal.fire({
            title: '<div style="color: white;">Send to MISC</div>',
            html: '<div style="color: white;">Are you sure you want to send these documents to MISC?</div>',
            showCancelButton: true,
            confirmButtonText: 'Yes, send it!',
            cancelButtonText: 'Cancel',
            confirmButtonColor: GreenBtnColor,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                setLoading(true);
                const formData = new FormData();
                uploadedDocuments.forEach((file) => {
                    formData.append('files', file);
                });
                formData.append('signatureRequestId', signatureRequestId.toString());
                apiClient('/SignatureRequests/uploadSignedDocument', {
                    method: 'POST',
                    headers: new Headers({
                        Authorization: `Bearer ${token}`,
                    }),
                    body: formData,
                })
                    .then((response) => {
                        if (!response) return;
                        if (response.status) {
                            Swal.fire({
                                title: 'Documents Sent to MISC',
                                text: 'Documents sent to MISC successfully.',
                                icon: 'success',
                                confirmButtonText: 'OK',
                            });
                            handleClose();
                            onSuccess();
                        } else {
                            Swal.fire({
                                title: 'Error',
                                text: response?.message || 'Failed to send documents to MISC',
                                icon: 'error',
                                confirmButtonText: 'OK',
                            });
                        }
                    })
                    .catch((error) => {
                        console.error('Error sending documents to MISC:', error);
                        Swal.fire({
                            title: 'Error',
                            text: 'Failed to send documents to MISC. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                        });
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            PaperProps={{
                sx: {
                    width: '90%',
                    maxWidth: 900,
                    minHeight: '40vh',
                    maxHeight: '90vh',
                    borderRadius: 2,
                    boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                },
            }}
            sx={{ '& .MuiDialog-paper': { overflow: 'hidden' } }}
        >
            {loading && <LinearProgress sx={{ flexShrink: 0 }} />}
            <DialogTitle
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    py: 2,
                    px: 3,
                    background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                    color: '#fff',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CloudUploadOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                    <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>
                        Upload Documents
                    </Typography>
                </Box>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        color: 'rgba(255,255,255,0.9)',
                        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.12)' },
                    }}
                    aria-label="Close dialog"
                >
                    <CloseOutlinedIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ px: 3, py: 2, overflow: 'auto' }}>
                <Typography mt={2} variant="overline" sx={sectionTitleSx}>
                    Upload signed documents
                </Typography>
                <Paper variant="outlined" sx={paperSectionSx}>
                    <FormHelperText className="formtexthelper-heading" sx={{ mt: 0, mb: 1 }}>
                        Select file(s) or drag and drop. You must upload at least one document, then click Send to MISC to complete.
                    </FormHelperText>
                    <StyledDropzone
                        label="Select File(s) or Drag and Drop"
                        onFileDrop={(files: File[]) => {
                            setUploadedDocuments((prev) => [...prev, ...files]);
                        }}
                    />
                    {uploadedDocuments.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <FormHelperText className="formtexthelper-heading" sx={{ mb: 1 }}>
                                Selected Files ({uploadedDocuments.length})
                            </FormHelperText>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {uploadedDocuments.map((file, index) => (
                                    <Chip
                                        key={`${file.name}-${index}`}
                                        label={file.name}
                                        onDelete={() => {
                                            setUploadedDocuments((prev) => prev.filter((_, i) => i !== index));
                                        }}
                                        deleteIcon={<CancelIcon />}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ fontWeight: 500 }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}
                </Paper>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: 1,
                    borderColor: 'divider',
                    backgroundColor: 'grey.50',
                    px: 3,
                    py: 2,
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                <Button
                    variant="outlined"
                    startIcon={<CloseOutlinedIcon />}
                    onClick={handleClose}
                    sx={{ fontWeight: 600, textTransform: 'none' }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    startIcon={<SendOutlinedIcon />}
                    onClick={handleSendToMisc}
                    disabled={uploadedDocuments.length === 0 || loading}
                    sx={{
                        backgroundColor: '#2099d2',
                        '&:hover': { backgroundColor: '#1a7ba8' },
                        fontWeight: 600,
                        textTransform: 'none',
                    }}
                >
                    Send to MISC
                </Button>
            </DialogActions>
        </Dialog>
    );
};
